import React from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { useTheme } from './ThemeContext';

/**
 * AIM's running man, reduced to a blocky silhouette and drawn with plain views
 * so there is no image asset to ship and it stays crisp at any scale.
 *
 * `size` is the box edge in points; every limb is positioned as a fraction of
 * it, so the figure scales cleanly from a 16px title-bar icon to the 64px
 * sign-on splash.
 */
export function RunningMan({
  size = 16,
  color,
  style,
}: {
  size?: number;
  color?: string;
  style?: StyleProp<ViewStyle>;
}) {
  const theme = useTheme();
  const fill = color ?? theme.colors.accent;
  const u = size / 16;
  const px = (n: number) => Math.max(1, Math.round(n * u));

  return (
    <View
      style={[{ width: size, height: size }, style]}
      accessibilityRole="image"
      accessibilityLabel="AIM"
    >
      {/* head */}
      <View
        style={[
          styles.part,
          { backgroundColor: fill, left: px(8), top: px(1), width: px(4), height: px(4), borderRadius: px(2) },
        ]}
      />
      {/* torso, leaning into the run */}
      <View
        style={[
          styles.part,
          {
            backgroundColor: fill,
            left: px(6),
            top: px(5),
            width: px(4),
            height: px(6),
            transform: [{ rotate: '-12deg' }],
          },
        ]}
      />
      {/* trailing arm */}
      <View
        style={[
          styles.part,
          { backgroundColor: fill, left: px(2), top: px(6), width: px(5), height: px(2), transform: [{ rotate: '20deg' }] },
        ]}
      />
      {/* leading arm */}
      <View
        style={[
          styles.part,
          { backgroundColor: fill, left: px(9), top: px(5), width: px(5), height: px(2), transform: [{ rotate: '-25deg' }] },
        ]}
      />
      {/* trailing leg */}
      <View
        style={[
          styles.part,
          { backgroundColor: fill, left: px(3), top: px(10), width: px(5), height: px(2), transform: [{ rotate: '35deg' }] },
        ]}
      />
      {/* leading leg */}
      <View
        style={[
          styles.part,
          { backgroundColor: fill, left: px(8), top: px(10), width: px(3), height: px(5), transform: [{ rotate: '-15deg' }] },
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  part: { position: 'absolute' },
});
